type SentryEventLike = {
  message?: string | null
  logentry?: {
    message?: string | null
    formatted?: string | null
  } | null
  exception?: {
    values?: Array<{
      type?: string | null
      value?: string | null
      mechanism?: {
        data?: {
          blocked_uri?: string | null
          violated_directive?: string | null
          source_file?: string | null
        } | null
      } | null
      stacktrace?: {
        frames?: Array<{
          filename?: string | null
          abs_path?: string | null
        }> | null
      } | null
    }> | null
  } | null
  request?: {
    url?: string | null
  } | null
}

type SentryEventHintLike = {
  originalException?: unknown
}

const REALTIME_AUTH_NOISE_MARKERS = [
  'websocket connection to',
  '/realtime/v1/websocket',
  'http authentication failed',
  'no valid credentials available',
] as const

// CSP violation noise: browser extensions, Google Translate, GTM-blocker rewrites, Perplexity.
// Own domains are intentionally excluded so first-party CSP violations still surface.
const CSP_NOISE_SCHEMES = ['blob:', 'eval:', 'data:']

const CSP_NOISE_DOMAIN_SUFFIXES = [
  '.google.com',
  '.gstatic.com',
  '.googletagmanager.com',
  '.perplexity.ai',
  // Chrome extension injections
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
]

const OWN_DOMAIN_SUFFIXES = ['teslalocksound.com']

// Message substrings that mark an error as browser/extension/network noise
// rather than a real defect in our code. Matched against both the caught
// exception's message and, where the SDK only surfaced a plain object,
// the reconstructed original-exception messages.
const NOISE_MESSAGE_FRAGMENTS = [
  'ResizeObserver',
  'Failed to fetch',
  // Safari's wording for the same network-level fetch failure Chrome reports as "Failed to fetch".
  'Load failed',
  // Firefox's wording for the same visitor-network fetch failure (TESLA-LOCK-SOUND-6K).
  'NetworkError when attempting to fetch resource',
  'AbortError',
  // WebExtension messaging API talking to its own background page — not our code.
  'Invalid call to runtime.sendMessage',
  // Extension (translator/ad-blocker) mutates the DOM under React, which then
  // fails to patch a node that moved. Not reproducible without the extension.
  "Failed to execute 'insertBefore' on 'Node'",
  "Failed to execute 'removeChild' on 'Node'",
  // AdSense TagError when the slot it claimed measured 0px. TES-1876 stops us
  // pushing into a collapsed <ins>, but adsbygoogle.js still throws this
  // asynchronously (TESLA-LOCK-SOUND-4R, 109 events). Visitor layout, not a
  // first-party defect.
  'No slot size for availableWidth',
  // Ad blockers (and strict tracking protection) fail the pagead2 script load.
  // The loader already treats this as a user-environment condition; keep the
  // SDK filter in case a remaining capture site reintroduces it (Sentry 4Z).
  'AdSense script failed to load',
  // CSP report-uri titles for eval: injections. The csp-report route now drops
  // these before Sentry; this catches the same signature if it lands in the
  // browser SDK (TESLA-LOCK-SOUND-Q).
  "Blocked 'script' from 'eval:'",
] as const

const NON_ERROR_REJECTION_UNDEFINED_PATTERN =
  /non-error promise rejection captured with value:\s*undefined\s*$/i

// Sentry's stack parser normalizes frames it cannot resolve a real origin for
// to the pseudo-scheme `app:///`. Injected extension bundles land there — but
// so does a great deal of our own code, which is why this rule has to be
// matched on a *positive* third-party signal rather than on "not our client
// bundle".
//
// Frames observed under `app:///` in this project:
//   app:///executors/200.js                 <- extension          (noise)
//   app:///injectScript.js                  <- extension          (noise)
//   app:///pagead/js/adsbygoogle.js         <- Google ad script   (noise)
//   app:///_next/static/chunks/<hash>.js    <- OUR client bundle  (keep)
//   app:///_next/server/chunks/<hash>.js    <- OUR server bundle  (keep)
//   app:///sounds, app:///blog/<slug>       <- OUR route frames   (keep)
//
// An earlier revision excluded only `_next/static` and treated everything
// else as third-party. That silently swallowed our own server-side errors —
// the `prune_funnel_events` RPC timeout and the `home:categories` 8s Supabase
// timeout both vanished from the error stream — because server frames live
// under `_next/server`, and Next.js labels SSR frames with the bare route
// path. Both exclusions below are therefore load-bearing:
//
//   1. anything under `_next/` is ours, client or server;
//   2. a frame must look like an actual script file (`.js`/`.mjs`, ignoring
//      any `?query`/`#hash`) to be called an injected bundle. Bare route
//      paths like `app:///sounds` are documents we rendered, not scripts
//      somebody injected.
const INJECTED_BUNDLE_SCHEME = 'app:///'
const OWN_BUNDLE_PATH_MARKER = '_next/'
const SCRIPT_FILE_PATTERN = /\.m?js$/i

function hasMessage(text: unknown, fragment: string): boolean {
  return typeof text === 'string' && text.includes(fragment)
}

function pushText(values: string[], candidate: unknown) {
  if (typeof candidate === 'string' && candidate.trim().length > 0) {
    values.push(candidate)
  }
}

function getOriginalExceptionMessages(originalException: unknown): string[] {
  const values: string[] = []

  if (typeof originalException === 'string') {
    pushText(values, originalException)
    return values
  }

  if (originalException instanceof Error) {
    pushText(values, originalException.name)
    pushText(values, originalException.message)
    return values
  }

  if (originalException && typeof originalException === 'object') {
    const originalRecord = originalException as Record<string, unknown>
    pushText(values, originalRecord.name)
    pushText(values, originalRecord.message)
    pushText(values, originalRecord.reason)
    pushText(values, originalRecord.detail)
  }

  return values
}

function getEventMessages(event: SentryEventLike): string[] {
  const values: string[] = []

  pushText(values, event.message)
  pushText(values, event.logentry?.message)
  pushText(values, event.logentry?.formatted)

  for (const exception of event.exception?.values ?? []) {
    pushText(values, exception.type)
    pushText(values, exception.value)
  }

  return values
}

function isOwnDomain(uri: string): boolean {
  return OWN_DOMAIN_SUFFIXES.some((suffix) => uri.includes(suffix))
}

function isThirdPartyCSPBlockedUri(blockedUri: string): boolean {
  if (!blockedUri || isOwnDomain(blockedUri)) return false
  if (CSP_NOISE_SCHEMES.some((scheme) => blockedUri.startsWith(scheme))) return true
  return CSP_NOISE_DOMAIN_SUFFIXES.some((suffix) => blockedUri.includes(suffix))
}

export function isThirdPartyCSPViolation(event: SentryEventLike): boolean {
  const exceptions = event.exception?.values ?? []
  for (const exc of exceptions) {
    if (exc.type !== 'CSPViolation') continue
    const blockedUri = exc.mechanism?.data?.blocked_uri ?? ''
    const sourceFile = exc.mechanism?.data?.source_file ?? ''
    // Drop if the blocked URI or injecting source file is third-party noise.
    if (isThirdPartyCSPBlockedUri(blockedUri)) return true
    if (sourceFile && isThirdPartyCSPBlockedUri(sourceFile)) return true
    // Extension injections often appear with empty/null blocked_uri but
    // `eval:` or `blob:` in the exception value string itself.
    const value = exc.value ?? ''
    if (CSP_NOISE_SCHEMES.some((s) => value.includes(s)) && !isOwnDomain(value)) return true
  }
  return false
}

export function isSupabaseRealtimeAuthNoiseMessage(message: string): boolean {
  const normalized = message.toLowerCase()
  return REALTIME_AUTH_NOISE_MARKERS.every((marker) => normalized.includes(marker))
}

export function isNonErrorRejectionWithUndefinedValue(event: SentryEventLike): boolean {
  return getEventMessages(event).some((message) =>
    NON_ERROR_REJECTION_UNDEFINED_PATTERN.test(message.trim()),
  )
}

function isInjectedBundleFrameUrl(url: string): boolean {
  if (!url.startsWith(INJECTED_BUNDLE_SCHEME)) return false
  if (url.includes(OWN_BUNDLE_PATH_MARKER)) return false

  const path = url.slice(INJECTED_BUNDLE_SCHEME.length).split(/[?#]/, 1)[0]
  return SCRIPT_FILE_PATTERN.test(path)
}

export function hasInjectedExtensionBundleFrame(event: SentryEventLike): boolean {
  for (const exception of event.exception?.values ?? []) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      const url = frame.filename ?? frame.abs_path ?? ''
      if (url && isInjectedBundleFrameUrl(url)) return true
    }
  }
  return false
}

const SHORT_GARBAGE_MESSAGE = /^[A-Za-z]{1,3}$/

function hasOwnBundleFrame(event: SentryEventLike): boolean {
  for (const exception of event.exception?.values ?? []) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      const url = frame.filename ?? frame.abs_path ?? ''
      if (url.includes(OWN_BUNDLE_PATH_MARKER)) return true
    }
  }
  return false
}

function normalizeGarbageCandidate(message: string): string {
  return message.replace(/^(Error|UnhandledRejection):\s*/i, '').trim()
}

export function isShortGarbageClientRejection(event: SentryEventLike): boolean {
  if (hasOwnBundleFrame(event)) return false
  return getEventMessages(event).some((message) =>
    SHORT_GARBAGE_MESSAGE.test(normalizeGarbageCandidate(message)),
  )
}

export function shouldIgnoreClientSentryEvent(
  event: SentryEventLike,
  hint: SentryEventHintLike,
): boolean {
  const originalException = hint.originalException
  const originalMessages = getOriginalExceptionMessages(originalException)

  if (
    originalException instanceof Error &&
    (originalException.name === 'AbortError' ||
      NOISE_MESSAGE_FRAGMENTS.some((fragment) => hasMessage(originalException.message, fragment)))
  ) {
    return true
  }

  const eventMessages = getEventMessages(event)
  if (
    [...originalMessages, ...eventMessages].some((message) =>
      NOISE_MESSAGE_FRAGMENTS.some((fragment) => hasMessage(message, fragment)),
    )
  ) {
    return true
  }

  if (isThirdPartyCSPViolation(event)) {
    return true
  }

  if (isNonErrorRejectionWithUndefinedValue(event)) {
    return true
  }

  if (hasInjectedExtensionBundleFrame(event)) {
    return true
  }

  if (isShortGarbageClientRejection(event)) {
    return true
  }

  return [...originalMessages, ...eventMessages].some(isSupabaseRealtimeAuthNoiseMessage)
}
